import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Share2, QrCode, Copy, Check } from "lucide-react";

const APP_URL = typeof window !== "undefined" ? window.location.origin : "https://thehostelnow.com";
const SHARE_TEXT = "TheHostelNow — verified student hostels from ₹250/night near every major coaching center in India.";

export default function ShareQRCard() {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dataUrl = await QRCode.toDataURL(APP_URL, {
          width: 640,
          margin: 2,
          errorCorrectionLevel: "H",
          color: { dark: "#9A3412", light: "#FFFFFF" },
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) { console.warn("QR generation failed:", err); }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = "thehostelnow-qr.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR code downloaded!");
  };

  const shareQr = async () => {
    const message = `${SHARE_TEXT} ${APP_URL}`;
    if (!qrDataUrl) {
      // At least try native share of URL
      if (navigator.share) {
        try { await navigator.share({ title: "TheHostelNow", text: SHARE_TEXT, url: APP_URL }); return; } catch {}
      }
      return;
    }
    try {
      const blob = await (await fetch(qrDataUrl)).blob();
      const file = new File([blob], "thehostelnow-qr.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "TheHostelNow QR Code",
          text: `Scan this to open TheHostelNow — ${APP_URL}`,
        });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: "TheHostelNow", text: SHARE_TEXT, url: APP_URL });
        return;
      }
    } catch (err) {
      if (err?.name !== "AbortError") console.warn("QR share failed:", err);
    }
    // Final fallback: copy the message
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Link copied — paste anywhere!");
    } catch {
      downloadQr();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div
        data-testid="share-qr-section"
        className="relative overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-amber-50 via-orange-50 to-orange-100 p-8 sm:p-12"
      >
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-orange-200 blur-[80px] opacity-70 pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-amber-200 blur-[80px] opacity-60 pointer-events-none" />

        <div className="relative grid md:grid-cols-2 gap-10 items-center">
          {/* Left — copy */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-orange-200 rounded-full px-3 py-1.5 text-[11px] tracking-widest uppercase font-bold text-orange-800">
              <QrCode size={12} /> Scan · Share · Book
            </div>
            <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-stone-900">
              Share <span className="text-orange-800">TheHostelNow</span> <br />
              with your <span className="text-amber-700">study group.</span>
            </h2>
            <p className="mt-4 text-slate-700 max-w-md leading-relaxed">
              Point any phone camera at the QR to open TheHostelNow.com instantly.
              Download the image and share it on WhatsApp groups, coaching notice boards, or hostel flyers.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                data-testid="qr-download-home-btn"
                onClick={downloadQr}
                className="rounded-full h-11 px-5 bg-orange-800 hover:bg-orange-900 text-white font-semibold gap-2"
              >
                <Download size={16} /> Download QR
              </Button>
              <Button
                data-testid="qr-share-home-btn"
                onClick={shareQr}
                variant="outline"
                className="rounded-full h-11 px-5 border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold gap-2"
              >
                <Share2 size={16} /> Share
              </Button>
              <Button
                data-testid="qr-copy-home-btn"
                onClick={copyLink}
                variant="outline"
                className="rounded-full h-11 px-5 border-orange-300 text-orange-800 hover:bg-orange-50 font-semibold gap-2"
              >
                {copied ? <><Check size={16} className="text-emerald-600" /> Copied</> : <><Copy size={16} /> Copy link</>}
              </Button>
            </div>
            <p className="mt-4 text-xs font-mono text-orange-900/70 break-all">{APP_URL}</p>
          </div>

          {/* Right — QR card */}
          <div className="flex md:justify-end">
            <div
              data-testid="qr-card-visual"
              className="relative w-full max-w-[320px] bg-white rounded-3xl shadow-xl border border-orange-200 p-6 text-center rotate-1 hover:rotate-0 transition-transform duration-300"
            >
              <div className="inline-flex items-center gap-1 bg-orange-100 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-orange-800">
                <QrCode size={11} /> Scan to open
              </div>
              <div className="mt-4 mx-auto aspect-square bg-white p-3 rounded-2xl border-2 border-dashed border-orange-200 grid place-items-center">
                {qrDataUrl ? (
                  <img
                    data-testid="home-qr-image"
                    src={qrDataUrl}
                    alt="TheHostelNow QR code"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-xs text-slate-400">Generating…</div>
                )}
              </div>
              <div className="mt-4 font-display font-black text-xl text-stone-900 tracking-tight">
                TheHostel<span className="text-orange-800">Now</span>
                <span className="text-amber-600">.com</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">Beds from ₹250/night · Aadhaar KYC</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
